import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import { useViewerStore } from '@stores/viewerStore';
import { useToastStore } from '@stores/toastStore';
import type { RendererController } from '@renderer/index';
import type { Transform } from '@mindcloudxai_3d/core';
import { rewriteGltfToBlobUrl } from './gltf-utils';

/**
 * 管理 asset 导入/删除/复制/transform 编辑与 viewer model API 的双向同步。
 * 调用方传入 getController 以获取当前会话的 RendererController。
 */
export function useAssetManager(getController: () => RendererController | null) {
  const syncFromModels = useAssetLibraryStore((s) => s.syncFromModels);
  const selectAsset = useAssetLibraryStore((s) => s.selectAsset);
  const updateAssetTransform = useAssetLibraryStore((s) => s.updateAssetTransform);
  const renameAsset = useAssetLibraryStore((s) => s.renameAsset);
  const setAssetSource = useAssetLibraryStore((s) => s.setAssetSource);
  const addToast = useToastStore((s) => s.addToast);
  const { t } = useTranslation();

  const importFile = useCallback(
    async (file: File) => {
      const ctrl = getController();
      if (!ctrl) return;
      try {
        const handle = await ctrl.model.addModel({ file, name: file.name });
        // 记录导入来源，供预览/导出 controller 重放
        setAssetSource(handle.id, { file });
      } catch (err) {
        console.error('Failed to import model:', err);
        addToast(t('assetLibrary.importFailed'), 'error');
      }
    },
    [getController, setAssetSource, addToast, t],
  );

  /** .gltf 文件夹导入：重写外部资源 URI 为 blob URL 后加载 */
  const importGltfFiles = useCallback(
    async (files: File[]) => {
      const ctrl = getController();
      if (!ctrl) return;
      try {
        const result = await rewriteGltfToBlobUrl(files);
        if (!result) {
          addToast(t('assetLibrary.importFailed'), 'error');
          return;
        }
        const handle = await ctrl.model.addModel({ url: result.url, name: result.name });
        // 存储 blob URL 作为来源，复制时可直接重新加载
        setAssetSource(handle.id, { url: result.url });
      } catch (err) {
        console.error('Failed to import gltf:', err);
        addToast(t('assetLibrary.importFailed'), 'error');
      }
    },
    [getController, setAssetSource, addToast, t],
  );

  const importUrl = useCallback(
    async (url: string, name?: string) => {
      const ctrl = getController();
      if (!ctrl) return;
      try {
        const handle = await ctrl.model.addModel({ url, name });
        setAssetSource(handle.id, { url });
      } catch (err) {
        console.error('Failed to import model:', err);
        addToast(t('assetLibrary.importFailed'), 'error');
      }
    },
    [getController, setAssetSource, addToast, t],
  );

  const removeAsset = useCallback(
    (id: string) => {
      const ctrl = getController();
      ctrl?.model.removeModel(id);
      selectAsset(null);
    },
    [getController, selectAsset],
  );

  /** 复制模型：调用 viewer duplicateModel 重新导入相同资源并偏移 position */
  const duplicateAsset = useCallback(
    async (id: string) => {
      const ctrl = getController();
      if (!ctrl) return;
      // 先取出原始 asset 的 source，duplicateModel 返回新 handle 后写入 store
      const originalAsset = useAssetLibraryStore.getState().assets.find((a) => a.id === id);
      try {
        const handle = await ctrl.model.duplicateModel(id);
        if (originalAsset?.source) {
          setAssetSource(handle.id, originalAsset.source);
        }
      } catch (err) {
        console.error('Failed to duplicate model:', err);
        addToast(t('assetLibrary.importFailed'), 'error');
      }
    },
    [getController, setAssetSource, addToast, t],
  );

  const setTransform = useCallback(
    (id: string, transform: Transform) => {
      const ctrl = getController();
      ctrl?.model.setModelTransform(id, transform);
      // 本地即时更新，不等 viewer 事件回流
      updateAssetTransform(id, transform);
    },
    [getController, updateAssetTransform],
  );

  const rename = useCallback(
    (id: string, name: string) => {
      renameAsset(id, name);
    },
    [renameAsset],
  );

  // 绑定 viewer model:changed 事件，同步到 store。
  // 通过 viewerStore.status 订阅感知场景重载（loading→ready 时 controller 被替换），
  // 避免轮询带来的资源浪费和事件空窗期。
  const bindModelEvents = useCallback(() => {
    const onModelChanged = (models: Parameters<typeof syncFromModels>[0]) => {
      syncFromModels(models);
    };
    const onModelTransform = (event: { modelId: string; transform: Transform }) => {
      updateAssetTransform(event.modelId, event.transform);
    };

    let currentCtrl: RendererController | null = null;

    const bind = (ctrl: RendererController | null) => {
      if (currentCtrl === ctrl) return;
      if (currentCtrl) {
        currentCtrl.off('model:changed', onModelChanged);
        currentCtrl.off('model:transform', onModelTransform);
      }
      currentCtrl = ctrl;
      if (ctrl) {
        ctrl.on('model:changed', onModelChanged);
        ctrl.on('model:transform', onModelTransform);
      }
    };

    bind(getController());

    // 订阅 viewerStore status 变化，场景重载时 controller 会被替换。
    // zustand 基础 subscribe 不支持 selector，在回调内比对 status 即可。
    let lastStatus = useViewerStore.getState().status;
    const unsubscribe = useViewerStore.subscribe((s) => {
      if (s.status !== lastStatus) {
        lastStatus = s.status;
        bind(getController());
      }
    });

    return () => {
      unsubscribe();
      if (currentCtrl) {
        currentCtrl.off('model:changed', onModelChanged);
        currentCtrl.off('model:transform', onModelTransform);
      }
    };
  }, [getController, syncFromModels, updateAssetTransform]);

  return {
    importFile,
    importGltfFiles,
    importUrl,
    removeAsset,
    duplicateAsset,
    setTransform,
    rename,
    bindModelEvents,
  };
}
